import { Flex, IconButton, Text, Tooltip } from '@lego/klik-ui';
import { Download, StatusInprogress, StatusSuccess, Trash } from '@lego/klik-ui/icons';
import type { MouseEvent } from 'react';
import { CopyableText } from '@/components/common/CopyableText';
import type { DeviceFile } from '@/hooks/device';
import { dateTimeFormat } from '@/utility/date';
import { createColumnHelper } from '../appTable';
import { dateRange } from '../filters/filterFunctions';

const columnHelper = createColumnHelper<DeviceFile>();

export const deviceFileTableColumns = (
  onDelete?: (fileId: number) => Promise<void>,
  onDownload?: (fileId: number, fileName: string) => Promise<void>
) =>
  columnHelper.columns([
    columnHelper.accessor<'id', number>('id', {
      cell: (info) => (
        <Text fontFamily="mono" fontSize="sm">
          {info.getValue()}
        </Text>
      ),
      filterFn: 'includesString',
      header: 'ID',
      meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
      size: 96,
    }),
    columnHelper.accessor<'fileName', string>('fileName', {
      cell: (info) => info.getValue(),
      header: 'Filename',
      filterFn: 'includesString',
      meta: { filterVariant: { type: 'singleSearch' } },
    }),
    columnHelper.accessor<'digest', string>('digest', {
      cell: (info) => (
        <CopyableText
          copyLabel="Copy full value"
          prefixLength={8}
          suffixLength={8}
          textProps={{ fontFamily: 'mono', fontSize: 'sm' }}
          truncationMode="prefixSuffix"
          value={info.getValue()}
        />
      ),
      header: 'Digest',
      filterFn: 'includesString',
      meta: { filterVariant: { type: 'singleSearch' } },
    }),
    columnHelper.accessor<'receivedAt', string | null>('receivedAt', {
      cell: (info) => {
        const value = info.getValue();
        if (!value)
          return (
            <Tooltip label={'The file has not been confirmed received by the device yet'}>
              <StatusInprogress color="grey" />
            </Tooltip>
          );

        const receivedAt = new Date(value);
        return (
          <Tooltip
            label={`The file was received by the device at ${receivedAt.toLocaleString(dateTimeFormat.local)}`}
          >
            <StatusSuccess color="green" />
          </Tooltip>
        );
      },
      header: 'Received on device',
      filterFn: (row, columnId, filterValue) => {
        if (filterValue === undefined) return true;
        const value = row.getValue(columnId);
        return filterValue ? !!value : !value;
      },
      meta: {
        filterVariant: {
          type: 'singleSelect',
          options: [
            { name: 'Received', filterValue: true },
            { name: 'Not Received', filterValue: false },
          ],
        },
      },
    }),
    columnHelper.accessor<'createdAt', string>('createdAt', {
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
      header: 'Created At',
      filterFn: dateRange,
      meta: { filterVariant: { type: 'dateRange' } },
    }),
    columnHelper.accessor<'updatedAt', string>('updatedAt', {
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
      header: 'Updated At',
      filterFn: dateRange,
      meta: { filterVariant: { type: 'dateRange' } },
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 100,
      meta: { isSticky: true },
      cell: ({ row }) => (
        <Flex gap={1}>
          {onDownload && (
            <IconButton
              aria-label="Download file"
              icon={<Download />}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onDownload && void onDownload(row.original.id, row.original.fileName);
              }}
              size="sm"
              variant="ghost"
            />
          )}
          {onDelete && (
            <IconButton
              aria-label="Delete file"
              icon={<Trash />}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onDelete && void onDelete(row.original.id).catch(() => {});
              }}
              size="sm"
              variant="ghost"
            />
          )}
        </Flex>
      ),
    }),
  ]);
